/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import {
  asRecord,
  asText,
  formatMutationLabel,
  formatTokenLabel,
  summarizeBlueprintCompileOperation,
} from './builderLifecycle';

type JsonRecord = Record<string, any>;

export interface BuilderPlanStatusView {
  tone: 'info' | 'success' | 'warning';
  title: string;
  body: string;
  chips: string[];
}

export interface BuilderPlanDetailView {
  title: string;
  rows: Array<{
    label: string;
    value: string;
  }>;
  note?: string | null;
}

export interface BuilderPlanReviewChecklistItem {
  label: string;
  status: 'done' | 'current' | 'pending' | 'blocked';
  detail?: string | null;
}

export interface BuilderPlanReviewView {
  title: string;
  checklist: BuilderPlanReviewChecklistItem[];
  focusTone: 'info' | 'success' | 'warning';
  focusSummary?: string | null;
  summaryChips: string[];
  nextStep: string;
}

export interface BuilderPlanActionView {
  tone: 'info' | 'success' | 'warning';
  title: string;
  summary: string;
  primaryLabel: string;
  primaryMessage: string;
  secondaryLabel?: string | null;
  secondaryMessage?: string | null;
  note?: string | null;
}

export interface BuilderPlanPreviewSection {
  title: string;
  items: string[];
}

export interface BuilderPlanPreviewView {
  title: string;
  summary?: string | null;
  sections: BuilderPlanPreviewSection[];
}

export interface BuilderPlanHealthView {
  tone: 'info' | 'success' | 'warning';
  title: string;
  summary?: string | null;
  sections: BuilderPlanPreviewSection[];
}

export interface BuilderPlanRevisionView {
  title: string;
  summary?: string | null;
  sections: BuilderPlanPreviewSection[];
}

export interface BuilderPlanDiffView {
  title: string;
  summary?: string | null;
  sections: BuilderPlanPreviewSection[];
}

interface BuilderPlanTargetOptions {
  fallbackSystemId?: number | null;
  fallbackSystemName?: string | null;
}

const ADVISORY_STAGES = new Set([
  'planning',
  'discovery_only',
  'draft_spec_only',
  'clarify_first',
  'quote_required',
  'refuse',
  'blueprint_ready',
]);

function asPositiveInt(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : 0;
}

function formatDiffValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'empty';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  const record = asRecord(value);
  if (record.hash) return `hash ${String(record.hash).slice(0, 10)}`;
  return 'changed';
}

function formatStatusSummary(stage: string, approvalState: string, applyStatus: string): string {
  if (stage === 'approval_pending') {
    return 'Waiting for your confirmation';
  }
  if (stage === 'ready_to_apply') {
    return 'Approved and ready to run';
  }
  if (stage === 'apply_started') {
    return 'Build apply is in progress';
  }
  if (stage === 'complete') {
    return 'Applied and verified';
  }
  if (stage === 'readback_failed') {
    return 'Apply ran, but verification still failed';
  }
  if (stage === 'apply_failed') {
    return 'Apply stopped before completion';
  }
  if (stage === 'validation_failed' || stage === 'needs_repair') {
    return 'Needs one more fix before it can run';
  }
  if (approvalState === 'confirmed') {
    return 'Approved and waiting for the next apply turn';
  }
  if (applyStatus === 'not_started') {
    return 'Saved for review';
  }
  return formatTokenLabel(stage || approvalState || applyStatus);
}

function formatRepairSummary(repairStatus: string, repairMateriality: string): string {
  if (!repairStatus || repairStatus === 'not_started') {
    return '';
  }
  if (repairStatus === 'non_material_repair_applied') {
    return 'Minor cleanup already applied';
  }
  if (repairStatus === 'material_repair_applied') {
    return repairMateriality === 'material'
      ? 'Meaningful repair applied and returned for review'
      : 'Repair applied and returned for review';
  }
  if (repairStatus === 'repair_required') {
    return 'Still needs one more repair pass';
  }
  return formatTokenLabel(repairStatus);
}

function formatCreditEstimate(value: unknown): string {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return '';
  }
  return parsed
    .toFixed(parsed >= 1 ? 2 : 3)
    .replace(/\.?0+$/, '');
}

function formatRunsPerMonth(value: unknown): string {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return '';
  }
  return `${parsed.toFixed(parsed >= 10 ? 0 : 1).replace(/\.?0+$/, '')} runs/month`;
}

function formatEstimateLine(
  label: string,
  estimatedCredits: unknown
): string {
  const parts: string[] = [];
  const credits = formatCreditEstimate(estimatedCredits);

  if (credits) {
    parts.push(`${credits} credits`);
  }
  if (parts.length === 0) {
    return '';
  }
  return `${label}: ${parts.join(', ')}`;
}

function buildEstimateBundlePreviewSections(value: unknown): BuilderPlanPreviewSection[] {
  const estimateBundle = asRecord(value);
  if (Object.keys(estimateBundle).length === 0) {
    return [];
  }

  const sections: BuilderPlanPreviewSection[] = [];
  const buildEffort = asRecord(estimateBundle.build_effort);
  const buildEffortItems: string[] = [];
  const estimateSummary = asText(estimateBundle.summary);
  if (estimateSummary) {
    buildEffortItems.push(estimateSummary);
  }

  const complexity = formatTokenLabel(buildEffort.complexity);
  if (complexity) {
    buildEffortItems.push(`${complexity} effort`);
  }

  const confidence = Number(buildEffort.confidence);
  if (Number.isFinite(confidence) && confidence > 0) {
    buildEffortItems.push(`Planner confidence: ${Math.round(confidence * 100)}%`);
  }

  if (buildEffort.buildable_now === true) {
    buildEffortItems.push('Ready to build now');
  } else if (buildEffort.buildable_now === false) {
    const missingDetails = normalizeStringList(buildEffort.missing_details);
    buildEffortItems.push(
      missingDetails.length > 0
        ? `Still missing: ${missingDetails.join(', ')}`
        : 'More detail is needed before apply.'
    );
  }

  if (buildEffortItems.length > 0) {
    sections.push({
      title: 'Build effort',
      items: buildEffortItems.slice(0, 4),
    });
  }

  const planning = asRecord(estimateBundle.planning);
  const oneTime = asRecord(estimateBundle.one_time);
  const perRun = asRecord(estimateBundle.per_run);
  const recurring = asRecord(estimateBundle.recurring);
  const lifecycleItems: string[] = [];

  const planningLine = formatEstimateLine(
    asText(planning.label) || 'Planning and design',
    planning.estimated_credits
  );
  if (planningLine) {
    lifecycleItems.push(planningLine);
  }

  const oneTimeLine = formatEstimateLine(
    asText(oneTime.label) || 'One-time build and apply',
    oneTime.estimated_credits
  );
  if (oneTimeLine) {
    lifecycleItems.push(oneTimeLine);
  }
  if (oneTime.reserve_required === true) {
    lifecycleItems.push('Credits are reserved before one-time build/apply starts.');
  }

  const perRunLine = formatEstimateLine(
    asText(perRun.label) || 'Projected per-run runtime',
    perRun.estimated_credits
  );
  if (perRunLine) {
    lifecycleItems.push(perRunLine);
  }

  const recurringLabel = asText(recurring.label) || 'Projected recurring runtime';
  const recurringLine = formatEstimateLine(
    recurringLabel,
    recurring.estimated_credits
  );
  const recurringSchedule = asText(recurring.schedule_description);
  const recurringRunsPerMonth = formatRunsPerMonth(recurring.runs_per_month);
  if (recurringLine) {
    const cadenceHints = [recurringSchedule, recurringRunsPerMonth].filter(Boolean);
    lifecycleItems.push(
      cadenceHints.length > 0 ? `${recurringLine} (${cadenceHints.join(', ')})` : recurringLine
    );
  } else if (recurringSchedule) {
    lifecycleItems.push(
      recurringRunsPerMonth
        ? `Recurring cadence: ${recurringSchedule} (${recurringRunsPerMonth}). Monthly estimate unavailable.`
        : `Recurring cadence noted: ${recurringSchedule}. Monthly estimate unavailable.`
    );
  }

  if (lifecycleItems.length > 0) {
    sections.push({
      title: 'Lifecycle estimate',
      items: lifecycleItems,
    });
  }

  return sections;
}

function normalizeStringList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((item) => asText(item)).filter(Boolean);
}

function summarizeChangedObject(item: JsonRecord): string {
  const explicit = asText(item.summary || item.label || item.description);
  if (explicit) {
    return explicit;
  }
  const type = formatTokenLabel(item.object_type || item.type || item.kind);
  const name = asText(item.name || item.key || item.ref);
  if (type && name) {
    return `${type}: ${name}`;
  }
  return type || name;
}

function formatExpertTypeLabel(value: unknown): string {
  const token = asText(value).toUpperCase();
  if (!token) {
    return '';
  }
  if (token === 'ANALYSIS') {
    return 'Analysis';
  }
  if (token === 'ACTION') {
    return 'Action';
  }
  if (token === 'CODE') {
    return 'Code';
  }
  return formatTokenLabel(token);
}

function summarizeBlueprintStatistics(statistics: JsonRecord): string {
  const parts: string[] = [];
  const expertCount = asPositiveInt(statistics.expert_count);
  const sharedInputCount = asPositiveInt(statistics.shared_input_count);
  const connectionCount = asPositiveInt(statistics.connection_count);

  if (expertCount > 0) {
    parts.push(`${expertCount} expert${expertCount === 1 ? '' : 's'}`);
  }
  if (sharedInputCount > 0) {
    parts.push(`${sharedInputCount} shared input${sharedInputCount === 1 ? '' : 's'}`);
  }
  if (connectionCount > 0) {
    parts.push(`${connectionCount} connection${connectionCount === 1 ? '' : 's'}`);
  }

  return parts.join(', ');
}

function summarizeBlueprintExpert(item: JsonRecord): string {
  const name = asText(item.name || item.key);
  const type = formatExpertTypeLabel(item.type);
  const role = formatTokenLabel(item.role);

  if (name && type) {
    return `${name} (${type})`;
  }
  if (name && role) {
    return `${name} (${role})`;
  }
  return name || type || role;
}

function summarizeBlueprintSharedInput(item: JsonRecord): string {
  const name = asText(item.name || item.key);
  const type = formatTokenLabel(item.type);

  if (name && type) {
    return `${name} (${type})`;
  }
  return name || type;
}

function summarizeRepairAction(item: JsonRecord): string {
  const message = asText(item.message);
  if (message) {
    return message;
  }
  const code = formatTokenLabel(item.code);
  const path = asText(item.path);
  if (code && path) {
    return `${code} at ${path}`;
  }
  return code || path;
}

function buildRevisionDiffSections(diffPayload: JsonRecord): BuilderPlanPreviewSection[] {
  const sections = Array.isArray(diffPayload.sections) ? diffPayload.sections : [];
  return sections
    .map((section) => {
      const sectionRecord = asRecord(section);
      const title = asText(sectionRecord.title) || formatTokenLabel(sectionRecord.group);
      const items = (Array.isArray(sectionRecord.changes) ? sectionRecord.changes : [])
        .map((change) => summarizeRevisionDiffChange(asRecord(change)))
        .filter(Boolean);
      return { title, items };
    })
    .filter((section) => section.title && section.items.length > 0)
    .slice(0, 8);
}

function summarizeRevisionDiffChange(change: JsonRecord): string {
  const changeType = formatTokenLabel(change.change_type) || 'Changed';
  const objectType = formatTokenLabel(change.object_type) || 'Item';
  const objectKey = asText(change.object_key) || asText(change.intent_item_id) || asText(change.component_ref);
  const fields = asRecord(change.fields);
  const fieldItems = Object.entries(fields)
    .map(([fieldName, diffValue]) => {
      const diffRecord = asRecord(diffValue);
      return `${formatTokenLabel(fieldName)} ${formatDiffValue(diffRecord.before)} -> ${formatDiffValue(diffRecord.after)}`;
    })
    .filter(Boolean);

  if (fieldItems.length > 0) {
    return `${changeType} ${objectType}${objectKey ? ` ${objectKey}` : ''}: ${fieldItems.slice(0, 3).join('; ')}`;
  }

  const before = asRecord(change.before);
  const after = asRecord(change.after);
  const beforeLabel = asText(before.name) || asText(before.status) || asText(before.capability_key) || formatDiffValue(before.hash ? before : null);
  const afterLabel = asText(after.name) || asText(after.status) || asText(after.capability_key) || formatDiffValue(after.hash ? after : null);
  const transition = beforeLabel || afterLabel ? ` (${beforeLabel || 'empty'} -> ${afterLabel || 'empty'})` : '';
  return `${changeType} ${objectType}${objectKey ? ` ${objectKey}` : ''}${transition}`;
}

function summarizeReadbackProblem(value: unknown): string {
  const text = asText(value);
  if (!text) {
    return '';
  }
  const colonIndex = text.indexOf(':');
  if (colonIndex === -1) {
    return formatTokenLabel(text);
  }
  const code = formatTokenLabel(text.slice(0, colonIndex));
  const detail = text.slice(colonIndex + 1).trim();
  return detail ? `${code}: ${detail}` : code;
}

function mergeBuilderPlanPayloadSources(...sources: Array<unknown>): JsonRecord | null {
  const merged: JsonRecord = {};

  sources.forEach((source) => {
    const record = asRecord(source);
    if (Object.keys(record).length > 0) {
      Object.assign(merged, record);
    }
  });

  return Object.keys(merged).length > 0 ? merged : null;
}

function normalizeBackendActionHint(value: unknown): BuilderPlanActionView | null {
  const hint = asRecord(value);
  const rawString = (input: unknown): string =>
    typeof input === 'string' ? input : '';
  const title = asText(hint.title);
  const summary = asText(hint.summary);
  const primaryLabel = asText(hint.primary_label || hint.primaryLabel);
  const primaryMessage = rawString(hint.primary_message || hint.primaryMessage);

  if (!title || !summary || !primaryLabel || !primaryMessage.trim()) {
    return null;
  }

  const toneValue = asText(hint.tone).toLowerCase();
  const tone: BuilderPlanActionView['tone'] =
    toneValue === 'warning' ? 'warning' : toneValue === 'success' ? 'success' : 'info';

  const secondaryLabel = asText(hint.secondary_label || hint.secondaryLabel);
  const secondaryMessage = rawString(
    hint.secondary_message || hint.secondaryMessage
  );

  return {
    tone,
    title,
    summary,
    primaryLabel,
    primaryMessage,
    secondaryLabel: secondaryLabel || null,
    secondaryMessage: secondaryMessage.trim() ? secondaryMessage : null,
    note: asText(hint.note) || null,
  };
}

function normalizeBackendStatusHint(value: unknown): BuilderPlanStatusView | null {
  const hint = asRecord(value);
  const title = asText(hint.title);
  const body = asText(hint.body);
  if (!title || !body) {
    return null;
  }
  const toneValue = asText(hint.tone).toLowerCase();
  const tone: BuilderPlanStatusView['tone'] =
    toneValue === 'warning' ? 'warning' : toneValue === 'success' ? 'success' : 'info';
  return {
    tone,
    title,
    body,
    chips: normalizeStringList(hint.chips),
  };
}

function normalizeBackendReviewHint(value: unknown): BuilderPlanReviewView | null {
  const hint = asRecord(value);
  const title = asText(hint.title);
  const nextStep = asText(hint.next_step || hint.nextStep);
  if (!title || !nextStep) {
    return null;
  }
  const checklist = (Array.isArray(hint.checklist) ? hint.checklist : [])
    .map((item) => asRecord(item))
    .map((item) => {
      const label = asText(item.label);
      const statusValue = asText(item.status).toLowerCase();
      if (!label) {
        return null;
      }
      const status: BuilderPlanReviewChecklistItem['status'] =
        statusValue === 'done'
          ? 'done'
          : statusValue === 'current'
            ? 'current'
            : statusValue === 'blocked'
              ? 'blocked'
              : 'pending';
      return {
        label,
        status,
        detail: asText(item.detail) || null,
      };
    })
    .filter(Boolean) as BuilderPlanReviewChecklistItem[];

  const focusToneValue = asText(hint.focus_tone || hint.focusTone).toLowerCase();
  const focusTone: BuilderPlanReviewView['focusTone'] =
    focusToneValue === 'warning'
      ? 'warning'
      : focusToneValue === 'success'
        ? 'success'
        : 'info';

  return {
    title,
    checklist,
    focusTone,
    focusSummary: asText(hint.focus_summary || hint.focusSummary) || null,
    summaryChips: normalizeStringList(hint.summary_chips || hint.summaryChips),
    nextStep,
  };
}

function hasCompiledOperations(
  source: JsonRecord,
  state: JsonRecord
): boolean {
  return asPositiveInt(source.compiled_operation_count ?? state.compiled_operation_count) > 0;
}

function isCompileReady(source: JsonRecord, state: JsonRecord): boolean {
  const compileStatus = asText(source.compile_status || state.compile_status);
  return compileStatus.startsWith('compiled') && hasCompiledOperations(source, state);
}

function deriveNextStep(stage: string, blocker: string): string {
  if (stage === 'approval_pending') {
    return 'Reply with a clear approval such as "proceed" to apply this saved plan.';
  }
  if (stage === 'ready_to_apply') {
    return 'The saved plan is approved. The next build turn can run the deterministic apply.';
  }
  if (stage === 'apply_started') {
    return 'The deterministic apply is already underway. The builder should report back after verification completes.';
  }
  if (stage === 'complete') {
    return 'This saved plan has already been applied and verified. You can now test it or iterate on changes.';
  }
  if (stage === 'validation_failed' || stage === 'needs_repair') {
    return blocker || 'The builder needs to repair the saved plan before it can be approved or applied.';
  }
  if (stage === 'readback_failed') {
    return blocker || 'The apply needs one more repair or retry pass before the saved graph matches the plan.';
  }
  if (stage === 'apply_failed') {
    return blocker || 'The apply stopped before completion. The next step is to fix the blocker and retry.';
  }
  return blocker || 'The builder can keep refining this saved plan until it is ready for approval.';
}

function resolveTargetLabel(
  targetSystemId: unknown,
  options?: BuilderPlanTargetOptions
): string {
  const fallbackSystemId = options?.fallbackSystemId ?? null;
  const fallbackSystemName = asText(options?.fallbackSystemName);
  const numericTarget =
    typeof targetSystemId === 'number' || typeof targetSystemId === 'string'
      ? Number(targetSystemId)
      : null;

  if (
    fallbackSystemName &&
    numericTarget !== null &&
    Number.isFinite(numericTarget) &&
    fallbackSystemId !== null &&
    numericTarget === fallbackSystemId
  ) {
    return `"${fallbackSystemName}"`;
  }
  if (fallbackSystemName && (targetSystemId === null || targetSystemId === undefined || targetSystemId === '')) {
    return `"${fallbackSystemName}"`;
  }
  if (numericTarget !== null && Number.isFinite(numericTarget)) {
    return `system ${numericTarget}`;
  }
  return fallbackSystemName ? `"${fallbackSystemName}"` : 'this system';
}

function resolveActionTargetName(
  source: JsonRecord,
  state: JsonRecord,
  payload: JsonRecord,
  options?: BuilderPlanTargetOptions
): string {
  const blueprintSystemName = asText(asRecord(asRecord(payload.blueprint).system).name);
  if (blueprintSystemName) {
    return blueprintSystemName;
  }

  const fallbackSystemId = options?.fallbackSystemId ?? null;
  const fallbackSystemName = asText(options?.fallbackSystemName);
  const targetSystemId = source.target_system_id ?? state.target_system_id;
  const numericTarget =
    typeof targetSystemId === 'number' || typeof targetSystemId === 'string'
      ? Number(targetSystemId)
      : null;

  if (
    fallbackSystemName &&
    numericTarget !== null &&
    Number.isFinite(numericTarget) &&
    fallbackSystemId !== null &&
    numericTarget === fallbackSystemId
  ) {
    return fallbackSystemName;
  }
  if (fallbackSystemName && (targetSystemId === null || targetSystemId === undefined || targetSystemId === '')) {
    return fallbackSystemName;
  }
  if (numericTarget !== null && Number.isFinite(numericTarget)) {
    return `system ${numericTarget}`;
  }
  return fallbackSystemName || 'this system';
}

function buildSavedPlanReference(targetName: string): string {
  return targetName === 'this system'
    ? 'this saved plan'
    : `the saved plan for ${targetName}`;
}

function buildApprovedSavedPlanReference(targetName: string): string {
  return targetName === 'this system'
    ? 'the approved saved plan for this system'
    : `the approved saved plan for ${targetName}`;
}

function hasPersistedSavedPlan(
  builderState: JsonRecord | null | undefined,
  planPayload?: JsonRecord | null | undefined
): boolean {
  const state = asRecord(builderState);
  const payload = asRecord(planPayload);
  const stateActiveSavedPlan = asRecord(state.active_saved_plan);
  const payloadActiveSavedPlan = asRecord(payload.active_saved_plan);

  return Boolean(
    asText(state.plan_artifact_id) ||
      asText(stateActiveSavedPlan.plan_artifact_id) ||
      asText(payload.plan_artifact_id) ||
      asText(payloadActiveSavedPlan.plan_artifact_id)
  );
}

export function extractBuilderStateFromExecutionDetail(
  execution: { metadata?: JsonRecord; planner_metadata?: JsonRecord } | null | undefined
): JsonRecord | null {
  if (!execution) {
    return null;
  }

  const metadata = asRecord(execution.metadata);
  const builderState = asRecord(metadata.builder_state);
  if (Object.keys(builderState).length > 0) {
    return builderState;
  }

  const routeAuditBuilderPlan = asRecord(asRecord(metadata.route_audit).builder_plan);
  if (Object.keys(routeAuditBuilderPlan).length > 0) {
    return routeAuditBuilderPlan;
  }

  const plannerMetadata = asRecord(execution.planner_metadata);
  const plannerExecutionPlan = asRecord(plannerMetadata.execution_plan);
  const builderContract = asRecord(plannerExecutionPlan.builder);
  return Object.keys(builderContract).length > 0 ? builderContract : null;
}

export function extractBuilderPlanPayloadFromExecutionDetail(
  execution: { metadata?: JsonRecord; planner_metadata?: JsonRecord } | null | undefined
): JsonRecord | null {
  if (!execution) {
    return null;
  }

  const metadata = asRecord(execution.metadata);
  const routeAudit = asRecord(metadata.route_audit);
  const builderPlan = asRecord(metadata.builder_plan);
  const blueprintCompileApply = asRecord(metadata.blueprint_compile_apply);
  const plannerMetadata = asRecord(execution.planner_metadata);
  const plannerExecutionPlan = asRecord(plannerMetadata.execution_plan);
  const builderContract = asRecord(plannerExecutionPlan.builder);
  const routeAuditBuilderPlan = asRecord(routeAudit.builder_plan);
  const routeAuditBlueprintCompileApply = asRecord(routeAudit.blueprint_compile_apply);
  const routeAuditBuilderBlueprint = asRecord(
    routeAudit.builder_blueprint || asRecord(asRecord(routeAudit.planning).builder_blueprint)
  );

  const mergedPayload = mergeBuilderPlanPayloadSources(
    builderContract,
    routeAuditBuilderPlan,
    builderPlan
  );

  if (!mergedPayload) {
    if (Object.keys(routeAuditBuilderBlueprint).length > 0) {
      return {
        blueprint: routeAuditBuilderBlueprint,
        blueprint_compile: asRecord(routeAuditBuilderBlueprint.compile),
      };
    }
    return null;
  }

  if (Object.keys(asRecord(mergedPayload.blueprint)).length === 0 && Object.keys(routeAuditBuilderBlueprint).length > 0) {
    mergedPayload.blueprint = routeAuditBuilderBlueprint;
  }
  if (
    Object.keys(asRecord(mergedPayload.blueprint_compile)).length === 0 &&
    Object.keys(asRecord(routeAuditBuilderBlueprint.compile)).length > 0
  ) {
    mergedPayload.blueprint_compile = asRecord(routeAuditBuilderBlueprint.compile);
  }
  if (
    Object.keys(asRecord(mergedPayload.blueprint_compile_apply)).length === 0 &&
    Object.keys(routeAuditBlueprintCompileApply).length > 0
  ) {
    mergedPayload.blueprint_compile_apply = routeAuditBlueprintCompileApply;
  }
  if (
    Object.keys(asRecord(mergedPayload.blueprint_compile_apply)).length === 0 &&
    Object.keys(blueprintCompileApply).length > 0
  ) {
    mergedPayload.blueprint_compile_apply = blueprintCompileApply;
  }

  return mergedPayload;
}

export function extractBuilderStateFromExecutionPlan(
  executionPlan: JsonRecord | null | undefined
): JsonRecord | null {
  const plan = asRecord(executionPlan);
  const builderContract = asRecord(plan.builder || asRecord(plan.execution_plan).builder);
  return Object.keys(builderContract).length > 0 ? builderContract : null;
}

export function extractBuilderPlanPayloadFromExecutionPlan(
  executionPlan: JsonRecord | null | undefined
): JsonRecord | null {
  const builderContract = extractBuilderStateFromExecutionPlan(executionPlan);
  return builderContract ? { ...builderContract } : null;
}

export function buildBuilderPlanStatusView(
  builderState: JsonRecord | null | undefined,
  options?: BuilderPlanTargetOptions
): BuilderPlanStatusView | null {
  const state = asRecord(builderState);
  if (Object.keys(state).length === 0) {
    return null;
  }

  const backendHint = normalizeBackendStatusHint(state.status_hint);
  if (backendHint) {
    return backendHint;
  }

  const activeSavedPlan = asRecord(state.active_saved_plan);
  const hasActiveSavedPlan = Boolean(asText(activeSavedPlan.plan_artifact_id));
  const stage = asText(state.stage);
  const approvalState = asText(state.approval_state);
  const activeSavedPlanState = asText(
    activeSavedPlan.approval_state || activeSavedPlan.plan_artifact_status
  );
  const targetLabel = resolveTargetLabel(
    activeSavedPlan.target_system_id ?? state.target_system_id,
    options
  );
  const compiledOperationCount = asPositiveInt(
    activeSavedPlan.compiled_operation_count ?? state.compiled_operation_count
  );
  const stateLabel = formatTokenLabel(activeSavedPlanState || approvalState || stage);
  const chips: string[] = [];
  const targetSystemId = activeSavedPlan.target_system_id ?? state.target_system_id;
  const numericTarget =
    typeof targetSystemId === 'number' || typeof targetSystemId === 'string'
      ? Number(targetSystemId)
      : null;

  if (
    numericTarget !== null &&
    Number.isFinite(numericTarget) &&
    options?.fallbackSystemId !== null &&
    options?.fallbackSystemId !== undefined &&
    numericTarget === options.fallbackSystemId
  ) {
    chips.push('Current system');
  } else if (numericTarget !== null && Number.isFinite(numericTarget)) {
    chips.push(`System ${numericTarget}`);
  }
  if (stateLabel) {
    chips.push(stateLabel);
  }
  if (compiledOperationCount > 0) {
    chips.push(
      `${compiledOperationCount} step${compiledOperationCount === 1 ? '' : 's'} ready`
    );
  }

  if (hasActiveSavedPlan && ADVISORY_STAGES.has(stage)) {
    const body =
      activeSavedPlanState === 'confirmed'
        ? `This reply was advisory only. Your saved build plan for ${targetLabel} is still active and already approved.`
        : `This reply was advisory only. Your saved build plan for ${targetLabel} is still active and waiting for confirmation.`;
    return {
      tone: activeSavedPlanState === 'confirmed' ? 'success' : 'info',
      title: 'Saved build plan still active',
      body,
      chips,
    };
  }

  if (stage === 'approval_pending') {
    return {
      tone: 'info',
      title: 'Saved build plan ready',
      body: `I already have a saved build plan for ${targetLabel}. Once you confirm, the builder can apply it.`,
      chips,
    };
  }

  if (stage === 'ready_to_apply') {
    return {
      tone: 'success',
      title: 'Approved build plan ready',
      body: `The saved build plan for ${targetLabel} is approved and ready to run. No changes were applied in this turn.`,
      chips,
    };
  }

  if (stage === 'validation_failed' || stage === 'needs_repair') {
    return {
      tone: 'warning',
      title: 'Saved build plan needs a fix',
      body: `A saved build plan exists for ${targetLabel}, but it needs one more repair pass before it can be approved or applied.`,
      chips,
    };
  }

  return null;
}

export function buildBuilderPlanDetailView(
  builderState: JsonRecord | null | undefined,
  options?: BuilderPlanTargetOptions
): BuilderPlanDetailView | null {
  const state = asRecord(builderState);
  if (Object.keys(state).length === 0 || !hasPersistedSavedPlan(state)) {
    return null;
  }

  const activeSavedPlan = asRecord(state.active_saved_plan);
  const source = Object.keys(activeSavedPlan).length > 0 ? activeSavedPlan : state;
  const stage = asText(state.stage);
  const approvalState = asText(source.approval_state || state.approval_state);
  const applyStatus = asText(state.apply_status);
  const targetLabel = resolveTargetLabel(source.target_system_id ?? state.target_system_id, options);
  const compiledOperationCount = asPositiveInt(
    source.compiled_operation_count ?? state.compiled_operation_count
  );
  const applyOperationCount = asPositiveInt(state.apply_operation_count);
  const appliedOperationCount = asPositiveInt(state.applied_operation_count);
  const repairSummary = formatRepairSummary(
    asText(state.repair_status),
    asText(state.repair_materiality)
  );
  const requestedMutationsRaw = Array.isArray(state.requested_mutations)
    ? state.requested_mutations
    : [];
  const requestedMutations = Array.from(
    new Set(
      requestedMutationsRaw
        .map((item) => formatMutationLabel(item))
        .filter((item) => Boolean(item))
    )
  );
  const rows: BuilderPlanDetailView['rows'] = [
    {
      label: 'Target',
      value:
        targetLabel.startsWith('"') && targetLabel.endsWith('"')
          ? targetLabel.slice(1, -1)
          : targetLabel.charAt(0).toUpperCase() + targetLabel.slice(1),
    },
    {
      label: 'Current posture',
      value: formatStatusSummary(stage, approvalState, applyStatus),
    },
  ];

  if (compiledOperationCount > 0) {
    rows.push({
      label: 'Deterministic steps',
      value: `${compiledOperationCount} ready`,
    });
  }

  if (applyOperationCount > 0 && (stage === 'apply_started' || appliedOperationCount > 0)) {
    rows.push({
      label: 'Apply progress',
      value: `${Math.min(appliedOperationCount, applyOperationCount)} of ${applyOperationCount} completed`,
    });
  }

  if (requestedMutations.length > 0) {
    rows.push({
      label: 'What it covers',
      value: requestedMutations.join(', '),
    });
  }

  if (repairSummary) {
    rows.push({
      label: 'Repairs',
      value: repairSummary,
    });
  }

  let note = asText(state.blocker);
  if (!note && stage === 'complete' && asText(state.readback_status) === 'valid') {
    note = 'Readback validation already confirmed the saved system graph.';
  } else if (!note && stage === 'ready_to_apply') {
    note = 'No changes were applied in this turn. The next approved build turn can apply the saved build plan.';
  } else if (!note && stage === 'approval_pending') {
    note = 'This saved plan is ready. One confirmation is enough to let the builder apply it.';
  }

  if (rows.length === 0 && !note) {
    return null;
  }

  return {
    title: 'Saved plan details',
    rows,
    note: note || null,
  };
}

export function buildBuilderPlanReviewView(
  builderState: JsonRecord | null | undefined
): BuilderPlanReviewView | null {
  const state = asRecord(builderState);
  if (Object.keys(state).length === 0) {
    return null;
  }

  if (!hasPersistedSavedPlan(state)) {
    return null;
  }

  const backendHint = normalizeBackendReviewHint(state.review_hint);
  if (backendHint) {
    return backendHint;
  }

  const activeSavedPlan = asRecord(state.active_saved_plan);
  const source = Object.keys(activeSavedPlan).length > 0 ? activeSavedPlan : state;
  const stage = asText(state.stage);
  const approvalState = asText(source.approval_state || state.approval_state);
  const blocker = asText(state.blocker);
  const compileReady = isCompileReady(source, state);
  const compiledOperationCount = asPositiveInt(
    source.compiled_operation_count || state.compiled_operation_count
  );
  const hasSavedPlan = Boolean(asText(source.plan_artifact_id || state.plan_artifact_id));
  const readbackStatus = asText(state.readback_status);

  const checklist: BuilderPlanReviewChecklistItem[] = [
    {
      label: 'Saved plan stored',
      status: hasSavedPlan ? 'done' : 'pending',
      detail: hasSavedPlan ? 'The builder is working from a persisted plan, not chat prose.' : null,
    },
    {
      label: 'Build steps prepared',
      status:
        stage === 'validation_failed' || stage === 'needs_repair'
          ? 'blocked'
          : compileReady
            ? 'done'
            : 'pending',
      detail:
        stage === 'validation_failed' || stage === 'needs_repair'
          ? 'The saved plan needs a repair pass before it can be applied.'
          : compileReady
            ? 'The saved build steps are ready.'
            : 'The builder still needs to prepare the saved build steps.',
    },
    {
      label: 'Approval checkpoint',
      status:
        approvalState === 'confirmed'
          ? 'done'
          : stage === 'approval_pending'
            ? 'current'
            : compileReady
              ? 'pending'
              : 'pending',
      detail:
        approvalState === 'confirmed'
          ? 'The saved plan has already been approved.'
          : stage === 'approval_pending'
            ? 'One clear confirmation will unlock apply for this saved plan.'
            : 'Approval becomes meaningful once the saved plan is fully reviewable.',
    },
    {
      label: 'Apply saved plan',
      status:
        stage === 'complete'
          ? 'done'
          : stage === 'apply_started'
            ? 'current'
            : stage === 'validation_failed' || stage === 'needs_repair'
              ? 'blocked'
            : stage === 'apply_failed' || stage === 'readback_failed'
              ? 'blocked'
              : stage === 'ready_to_apply'
                ? 'current'
                : 'pending',
      detail:
        stage === 'complete'
          ? 'Apply finished and the saved graph matched the expected plan.'
          : stage === 'apply_started'
          ? 'The saved plan is being applied now.'
            : stage === 'validation_failed' || stage === 'needs_repair'
              ? 'Apply is blocked until the saved plan is repaired and reviewable again.'
            : stage === 'apply_failed' || stage === 'readback_failed'
              ? blocker || 'Apply needs another repair or retry pass.'
              : stage === 'ready_to_apply'
                ? 'The next approved build turn can apply the saved plan.'
                : 'Apply will stay blocked until the plan is approved and ready.',
    },
    {
      label: 'Readback verification',
      status:
        stage === 'complete' && readbackStatus === 'valid'
          ? 'done'
          : stage === 'readback_failed'
            ? 'blocked'
            : stage === 'validation_failed' || stage === 'needs_repair' || stage === 'apply_failed'
              ? 'blocked'
            : stage === 'apply_started'
              ? 'pending'
              : 'pending',
      detail:
        stage === 'complete' && readbackStatus === 'valid'
          ? 'The saved system graph was verified against the plan after apply.'
          : stage === 'readback_failed'
            ? blocker || 'Verification found a mismatch after apply.'
            : stage === 'validation_failed' || stage === 'needs_repair'
              ? 'Verification cannot run until the saved plan is repaired and applied.'
            : stage === 'apply_failed'
              ? blocker || 'Verification cannot complete until apply succeeds.'
            : 'Verification runs after the saved plan is applied.',
    },
  ];

  const statusCounts = checklist.reduce(
    (acc, item) => {
      acc[item.status] += 1;
      return acc;
    },
    { done: 0, current: 0, pending: 0, blocked: 0 }
  );

  const summaryChips: string[] = [];
  if (compiledOperationCount > 0) {
    summaryChips.push(`${compiledOperationCount} step${compiledOperationCount === 1 ? '' : 's'} ready`);
  }
  if (statusCounts.done > 0) {
    summaryChips.push(`${statusCounts.done} done`);
  }
  if (statusCounts.current > 0) {
    summaryChips.push(`${statusCounts.current} current`);
  }
  if (statusCounts.pending > 0) {
    summaryChips.push(`${statusCounts.pending} pending`);
  }
  if (statusCounts.blocked > 0) {
    summaryChips.push(`${statusCounts.blocked} blocked`);
  }

  let focusTone: BuilderPlanReviewView['focusTone'] = 'info';
  let focusSummary = '';
  if (statusCounts.blocked > 0) {
    focusTone = 'warning';
    focusSummary =
      blocker || 'This saved plan is still blocked by repair or verification issues.';
  } else if (stage === 'approval_pending') {
    focusSummary =
      'This saved plan is reviewable and waiting for one clear approval before apply can begin.';
  } else if (stage === 'ready_to_apply') {
    focusSummary =
      'This saved plan is approved and ready to apply.';
  } else if (stage === 'apply_started') {
    focusSummary =
      'The saved build plan is being applied now.';
  } else if (stage === 'complete') {
    focusTone = 'success';
    focusSummary =
      'This saved plan has already been applied and verified successfully.';
  } else {
    focusSummary =
      'This checklist shows where the saved build plan is in its review and apply flow.';
  }

  return {
    title: 'Saved plan review',
    checklist,
    focusTone,
    focusSummary,
    summaryChips,
    nextStep: deriveNextStep(stage, blocker),
  };
}

export function buildBuilderPlanActionView(
  builderState: JsonRecord | null | undefined,
  planPayload?: JsonRecord | null,
  options?: BuilderPlanTargetOptions
): BuilderPlanActionView | null {
  const state = asRecord(builderState);
  if (Object.keys(state).length === 0 || !hasPersistedSavedPlan(state, planPayload)) {
    return null;
  }

  const payload = asRecord(planPayload);
  const backendHint = normalizeBackendActionHint(
    state.action_hint || payload.action_hint
  );
  if (backendHint) {
    return backendHint;
  }
  const activeSavedPlan = asRecord(state.active_saved_plan);
  const hasActiveSavedPlan = Object.keys(activeSavedPlan).length > 0;
  const source = hasActiveSavedPlan ? activeSavedPlan : state;
  const stateStage = asText(state.stage);
  const explicitSourceStage = asText(source.stage);
  const approvalState = asText(source.approval_state || state.approval_state);
  const blocker = asText(state.blocker || source.blocker);
  const repairStatus = asText(source.repair_status || state.repair_status);
  const targetName = resolveActionTargetName(source, state, payload, options);
  const savedPlanReference = buildSavedPlanReference(targetName);
  const approvedSavedPlanReference = buildApprovedSavedPlanReference(targetName);

  let effectiveStage = explicitSourceStage || stateStage;
  if (!explicitSourceStage && hasActiveSavedPlan && ADVISORY_STAGES.has(stateStage)) {
    effectiveStage =
      approvalState === 'confirmed'
        ? 'ready_to_apply'
        : repairStatus === 'repair_required'
          ? 'needs_repair'
          : 'approval_pending';
  }

  if (effectiveStage === 'approval_pending') {
    return {
      tone: 'info',
      title: 'Suggested reply',
      summary:
        `When you are happy with ${savedPlanReference}, use one clear approval reply so the Architect can start deterministic apply.`,
      primaryLabel: 'Use approval reply',
      primaryMessage: `Approved - please apply ${savedPlanReference}.`,
      secondaryLabel: 'Ask for one change',
      secondaryMessage: `Please revise ${savedPlanReference}: `,
      note: 'We will place the reply into chat so you can edit it before sending.',
    };
  }

  if (effectiveStage === 'ready_to_apply') {
    return {
      tone: 'success',
      title: 'Suggested reply',
      summary:
        `${savedPlanReference.charAt(0).toUpperCase() + savedPlanReference.slice(1)} is already approved. The next useful move is to ask the Architect to run the deterministic apply now.`,
      primaryLabel: 'Use apply reply',
      primaryMessage: `Please run ${approvedSavedPlanReference} now.`,
      secondaryLabel: 'Ask one final question',
      secondaryMessage: `Before you apply ${savedPlanReference}, I want to clarify: `,
      note: 'We will place the reply into chat so you can edit it before sending.',
    };
  }

  if (effectiveStage === 'validation_failed' || effectiveStage === 'needs_repair') {
    return {
      tone: 'warning',
      title: 'Suggested reply',
      summary:
        blocker ||
        `${savedPlanReference.charAt(0).toUpperCase() + savedPlanReference.slice(1)} needs one more repair pass. Ask the Architect to repair it and bring it back for review.`,
      primaryLabel: 'Use repair reply',
      primaryMessage: `Please repair ${savedPlanReference} and bring it back for review.`,
      secondaryLabel: 'Ask what is blocked',
      secondaryMessage: `Can you explain exactly what is still blocking ${savedPlanReference}?`,
      note: 'We will place the reply into chat so you can edit it before sending.',
    };
  }

  if (effectiveStage === 'apply_failed' || effectiveStage === 'readback_failed') {
    return {
      tone: 'warning',
      title: 'Suggested reply',
      summary:
        blocker ||
        `The deterministic apply for ${savedPlanReference} stopped short. Ask the Architect to fix the blocker and retry it.`,
      primaryLabel: 'Use retry reply',
      primaryMessage: `Please fix the issue on ${savedPlanReference} and retry the deterministic apply.`,
      secondaryLabel: 'Ask for the blocker',
      secondaryMessage: `Please explain the blocker on ${savedPlanReference} apply.`,
      note: 'We will place the reply into chat so you can edit it before sending.',
    };
  }

  if (effectiveStage === 'complete') {
    return {
      tone: 'success',
      title: 'Suggested reply',
      summary:
        `${savedPlanReference.charAt(0).toUpperCase() + savedPlanReference.slice(1)} has already been applied and verified. The next useful move is usually testing or iterating.`,
      primaryLabel: 'Use test reply',
      primaryMessage: `Please help me test ${targetName} now that the saved plan is applied.`,
      secondaryLabel: 'Ask for one change',
      secondaryMessage: `I want to revise ${targetName}: `,
      note: 'We will place the reply into chat so you can edit it before sending.',
    };
  }

  return null;
}

export function buildBuilderPlanPreviewView(
  planPayload: JsonRecord | null | undefined,
  builderState: JsonRecord | null | undefined
): BuilderPlanPreviewView | null {
  const payload = asRecord(planPayload);
  const state = asRecord(builderState);
  if (
    (Object.keys(payload).length === 0 && Object.keys(state).length === 0) ||
    !hasPersistedSavedPlan(state, payload)
  ) {
    return null;
  }

  const blueprint = asRecord(payload.blueprint);
  const blueprintSystem = asRecord(blueprint.system);
  const blueprintStatistics = asRecord(blueprint.statistics);
  const blueprintCompile = asRecord(payload.blueprint_compile || blueprint.compile);

  const operationItems = (Array.isArray(payload.operation_list) ? payload.operation_list : [])
    .map((item) => asRecord(item))
    .map((item) => asText(item.summary || item.description || item.label))
    .filter(Boolean);

  const blueprintCompileItems = (
    Array.isArray(blueprintCompile.operations) ? blueprintCompile.operations : []
  )
    .map((item) => summarizeBlueprintCompileOperation(asRecord(item)))
    .filter(Boolean);

  const changedObjectItems = (Array.isArray(payload.changed_objects) ? payload.changed_objects : [])
    .map((item) => summarizeChangedObject(asRecord(item)))
    .filter(Boolean);

  const requestedMutations = normalizeStringList(
    payload.requested_mutations ?? state.requested_mutations
  ).map((item) => formatMutationLabel(item));

  const sections: BuilderPlanPreviewSection[] = [];
  const systemItems: string[] = [];
  const systemName = asText(blueprintSystem.name || blueprintSystem.key);
  const systemDescription = asText(blueprintSystem.description);
  const systemStatistics = summarizeBlueprintStatistics(blueprintStatistics);
  const compiledOperationCount = asPositiveInt(
    blueprintCompile.operation_count ?? payload.operation_count ?? state.compiled_operation_count
  );

  if (systemName) {
    systemItems.push(`System: ${systemName}`);
  }
  if (systemStatistics) {
    systemItems.push(systemStatistics);
  }
  if (compiledOperationCount > 0) {
    systemItems.push(
      `${compiledOperationCount} build step${compiledOperationCount === 1 ? '' : 's'} prepared`
    );
  }
  if (systemDescription) {
    systemItems.push(systemDescription);
  }
  if (systemItems.length > 0) {
    sections.push({
      title: 'System outline',
      items: systemItems.slice(0, 4),
    });
  }

  const expertItems = (Array.isArray(blueprint.experts) ? blueprint.experts : [])
    .map((item) => summarizeBlueprintExpert(asRecord(item)))
    .filter(Boolean);
  if (expertItems.length > 0) {
    sections.push({
      title: 'Experts included',
      items: expertItems.slice(0, 8),
    });
  }

  const sharedInputItems = (Array.isArray(blueprint.shared_inputs) ? blueprint.shared_inputs : [])
    .map((item) => summarizeBlueprintSharedInput(asRecord(item)))
    .filter(Boolean);
  if (sharedInputItems.length > 0) {
    sections.push({
      title: 'Shared inputs',
      items: sharedInputItems.slice(0, 8),
    });
  }

  if (operationItems.length > 0) {
    sections.push({
      title: 'Planned steps',
      items: operationItems.slice(0, 8),
    });
  } else if (blueprintCompileItems.length > 0) {
    sections.push({
      title: 'Planned steps',
      items: blueprintCompileItems.slice(0, 8),
    });
  } else if (requestedMutations.length > 0) {
    sections.push({
      title: 'Planned steps',
      items: requestedMutations,
    });
  }

  if (changedObjectItems.length > 0) {
    sections.push({
      title: 'Objects affected',
      items: changedObjectItems.slice(0, 8),
    });
  }

  const estimateBundleSections = buildEstimateBundlePreviewSections(payload.estimate_bundle);
  if (estimateBundleSections.length > 0) {
    sections.push(...estimateBundleSections);
  } else {
    const quoteSummary = asRecord(payload.quote_summary);
    const estimateItems: string[] = [];
    const estimatedCredits = formatCreditEstimate(
      payload.estimated_credits ?? quoteSummary.estimated_credits
    );
    if (estimatedCredits) {
      estimateItems.push(`${estimatedCredits} credits estimated`);
    }
    if (payload.reserve_required === true || quoteSummary.reserve_required === true) {
      estimateItems.push('Reserve confirmation required');
    }
    if (estimateItems.length > 0) {
      sections.push({
        title: 'Estimate',
        items: estimateItems,
      });
    }
  }

  if (sections.length === 0) {
    return null;
  }

  const stage = asText(state.stage);
  let summary = '';
  if (stage === 'approval_pending') {
    summary = 'These are the saved build changes waiting for your confirmation.';
  } else if (stage === 'ready_to_apply') {
    summary = 'These saved build changes are approved and ready for the next apply turn.';
  } else if (stage === 'apply_started') {
    summary = 'This is the saved change set currently being applied.';
  } else if (stage === 'complete') {
    summary = 'This is the change set that was saved, applied, and verified.';
  } else if (stage === 'validation_failed' || stage === 'needs_repair') {
    summary = 'This saved change set is preserved, but it still needs one more repair pass before apply.';
  }

  return {
    title: 'Saved plan preview',
    summary: summary || null,
    sections,
  };
}

export function buildBuilderPlanHealthView(
  planPayload: JsonRecord | null | undefined,
  builderState: JsonRecord | null | undefined
): BuilderPlanHealthView | null {
  const payload = asRecord(planPayload);
  const state = asRecord(builderState);
  if (
    (Object.keys(payload).length === 0 && Object.keys(state).length === 0) ||
    !hasPersistedSavedPlan(state, payload)
  ) {
    return null;
  }

  const blueprint = asRecord(payload.blueprint);
  const repairSummary = asRecord(payload.repair_summary);
  const compileApply = asRecord(payload.blueprint_compile_apply);
  const readbackValidation = asRecord(compileApply.readback_validation);
  const repairAttempt = asRecord(compileApply.repair_attempt);
  const stage = asText(state.stage);
  const sections: BuilderPlanPreviewSection[] = [];

  const validationItems: string[] = [];
  normalizeStringList(blueprint.validation_errors).forEach((item) => {
    validationItems.push(formatTokenLabel(item));
  });
  normalizeStringList(repairSummary.unresolved_error_codes).forEach((item) => {
    validationItems.push(`Still unresolved: ${formatTokenLabel(item)}`);
  });
  normalizeStringList(repairSummary.warning_codes).forEach((item) => {
    validationItems.push(`Watch: ${formatTokenLabel(item)}`);
  });
  if (validationItems.length > 0) {
    sections.push({
      title: 'Validation and warnings',
      items: validationItems.slice(0, 8),
    });
  }

  const repairItems: string[] = [];
  const repairStatus = asText(repairSummary.status || state.repair_status);
  if (repairStatus) {
    const summary = formatRepairSummary(
      repairStatus,
      asText(repairSummary.materiality || state.repair_materiality)
    );
    if (summary) {
      repairItems.push(summary);
    }
  }
  if (repairSummary.requires_fresh_review === true) {
    repairItems.push('Fresh review is required because the saved plan changed materially.');
  }
  if (repairSummary.candidate_preserved === true) {
    repairItems.push('The original candidate was preserved so the builder can compare repairs against it.');
  }
  (Array.isArray(repairSummary.actions) ? repairSummary.actions : [])
    .map((item) => summarizeRepairAction(asRecord(item)))
    .filter(Boolean)
    .slice(0, 6)
    .forEach((item) => {
      repairItems.push(item);
    });
  if (repairItems.length > 0) {
    sections.push({
      title: 'Repairs applied',
      items: repairItems,
    });
  }

  const verificationItems: string[] = [];
  const readbackStatus = asText(readbackValidation.status || state.readback_status);
  if (readbackStatus === 'valid' || (stage === 'complete' && readbackStatus === 'valid')) {
    verificationItems.push('Deterministic apply readback matched the saved plan.');
  }
  normalizeStringList(readbackValidation.problems)
    .map((item) => summarizeReadbackProblem(item))
    .filter(Boolean)
    .slice(0, 6)
    .forEach((item) => {
      verificationItems.push(`Readback issue: ${item}`);
    });
  if (repairAttempt.attempted === true) {
    const success =
      repairAttempt.success === true ||
      asText(asRecord(repairAttempt.readback_validation_after).status) === 'valid';
    const reason = asText(repairAttempt.reason);
    verificationItems.push(
      success
        ? `Automatic verification repair succeeded${reason ? ` (${formatTokenLabel(reason)})` : ''}.`
        : `Automatic verification repair was attempted${reason ? ` (${formatTokenLabel(reason)})` : ''}, but more work is still needed.`
    );
  }
  if (verificationItems.length > 0) {
    sections.push({
      title: 'Verification',
      items: verificationItems,
    });
  }

  if (sections.length === 0) {
    return null;
  }

  let tone: BuilderPlanHealthView['tone'] = 'info';
  if (
    validationItems.some((item) => item.startsWith('Still unresolved:')) ||
    readbackStatus === 'invalid' ||
    stage === 'validation_failed' ||
    stage === 'needs_repair' ||
    stage === 'readback_failed'
  ) {
    tone = 'warning';
  } else if (
    sections.length > 0 &&
    validationItems.length === 0 &&
    repairSummary.repair_required !== true &&
    (readbackStatus === 'valid' || stage === 'complete')
  ) {
    tone = 'success';
  }

  let summary = '';
  if (tone === 'warning') {
    summary = 'This saved plan is still preserved, but there are validation, repair, or verification details worth reviewing before you trust the next apply.';
  } else if (tone === 'success') {
    summary = 'The saved plan has clean repair and verification posture right now.';
  } else {
    summary = 'This is the saved plan health trail the builder is keeping behind the scenes.';
  }

  return {
    tone,
    title: 'Saved plan health',
    summary,
    sections,
  };
}

export function buildBuilderPlanRevisionView(
  planPayload: JsonRecord | null | undefined
): BuilderPlanRevisionView | null {
  const payload = asRecord(planPayload);
  const revisionDiffPayload = asRecord(payload.revision_diff_payload);
  const revisionDiffSections = buildRevisionDiffSections(revisionDiffPayload);
  if (revisionDiffSections.length > 0) {
    const summaryRecord = asRecord(revisionDiffPayload.summary);
    const changeCount = asPositiveInt(summaryRecord.change_count);
    const affectedIntentItemCount = asPositiveInt(summaryRecord.affected_intent_item_count);
    const incrementalRebuild = asRecord(payload.incremental_rebuild || revisionDiffPayload.incremental_rebuild);
    const incrementalItems: string[] = [];
    if (incrementalRebuild.applied_to_compile === true) {
      incrementalItems.push(
        `Compiled ${asPositiveInt(incrementalRebuild.compiled_operation_count)} changed operations and reused ${asPositiveInt(incrementalRebuild.skipped_operation_ids?.length)} unchanged operations.`
      );
    } else if (incrementalRebuild.safe === true) {
      incrementalItems.push('This revision is safe for affected-component rebuilds.');
    }
    const fullRebuildReasons = normalizeStringList(incrementalRebuild.requires_full_rebuild_reasons);
    if (fullRebuildReasons.length > 0) {
      incrementalItems.push(`Full rebuild required: ${fullRebuildReasons.map(formatTokenLabel).join(', ')}.`);
    }

    return {
      title: 'Revision diff',
      summary: [
        changeCount ? `${changeCount} grouped change${changeCount === 1 ? '' : 's'}` : '',
        affectedIntentItemCount ? `${affectedIntentItemCount} affected requirement${affectedIntentItemCount === 1 ? '' : 's'}` : '',
      ].filter(Boolean).join(' across ') || 'Grouped changes from the durable revision artifact.',
      sections: incrementalItems.length > 0
        ? [...revisionDiffSections, { title: 'Incremental rebuild', items: incrementalItems }]
        : revisionDiffSections,
    };
  }

  if (Object.keys(payload).length === 0 || !hasPersistedSavedPlan(null, payload)) {
    return null;
  }

  const repairSummary = asRecord(payload.repair_summary);
  const resolutionSummary = asRecord(payload.resolution_summary);
  const repairActions = (Array.isArray(repairSummary.actions) ? repairSummary.actions : [])
    .map((item) => asRecord(item));
  const resolvedFromContext = Array.from(
    new Set(
      [
        ...(Array.isArray(resolutionSummary.resolved_from_context)
          ? resolutionSummary.resolved_from_context
          : []),
        ...repairActions.filter((item) => {
          const source = asText(item.source);
          return source === 'context_resolution' || source === 'scope_resolution';
        }),
      ]
        .map((item) => summarizeRepairAction(asRecord(item)))
        .filter(Boolean)
    )
  );

  const adjustedDuringRepair = repairActions
    .filter((item) => {
      const source = asText(item.source);
      return source !== 'context_resolution' && source !== 'scope_resolution';
    })
    .map((item) => summarizeRepairAction(item))
    .filter(Boolean);

  const reviewImplications: string[] = [];
  if (repairSummary.requires_fresh_review === true) {
    reviewImplications.push('Fresh approval review is required because the saved plan changed in a meaningful way.');
  }
  if (repairSummary.candidate_preserved === true) {
    reviewImplications.push('The original draft was preserved so the builder can keep a trustworthy comparison trail.');
  }
  if (repairSummary.candidate_matches_normalized === true) {
    reviewImplications.push('The saved reviewable plan still matches the original draft content.');
  }

  const sections: BuilderPlanPreviewSection[] = [];
  if (resolvedFromContext.length > 0) {
    sections.push({
      title: 'Filled in from context',
      items: resolvedFromContext.slice(0, 8),
    });
  }
  if (adjustedDuringRepair.length > 0) {
    sections.push({
      title: 'Adjusted during repair',
      items: adjustedDuringRepair.slice(0, 8),
    });
  }
  if (reviewImplications.length > 0) {
    sections.push({
      title: 'Review implications',
      items: reviewImplications,
    });
  }

  if (sections.length === 0) {
    return null;
  }

  return {
    title: 'Saved plan revisions',
    summary:
      'These are the concrete revisions the builder made while turning the original saved draft into the current reviewable plan.',
    sections,
  };
}

export function buildBuilderPlanDiffView(
  planPayload: JsonRecord | null | undefined
): BuilderPlanDiffView | null {
  const payload = asRecord(planPayload);
  if (Object.keys(payload).length === 0 || !hasPersistedSavedPlan(null, payload)) {
    return null;
  }

  const diffSummary = asRecord(payload.candidate_diff_summary);
  const sections = (Array.isArray(diffSummary.sections) ? diffSummary.sections : [])
    .map((section) => {
      const record = asRecord(section);
      return {
        title: asText(record.title),
        items: normalizeStringList(record.items),
      };
    })
    .filter((section) => section.title && section.items.length > 0);

  const candidatePreserved = diffSummary.candidate_preserved === true;
  const candidateMatchesNormalized = diffSummary.candidate_matches_normalized === true;

  if (!candidatePreserved && sections.length === 0) {
    return null;
  }

  let summary = '';
  if (candidateMatchesNormalized) {
    summary =
      'The current reviewable plan still matches the original saved draft.';
  } else if (candidatePreserved) {
    summary =
      'This shows the field-level differences between the original saved draft and the current normalized plan.';
  }

  return {
    title: 'Saved plan diff',
    summary: summary || null,
    sections,
  };
}
